import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns/format';

import {
  CalendarEventClickEvent,
  RoleType,
  RoomServicesEnum,
  WeekCalendarComponent,
} from '@bookly/shared';

import { CalendarFiltersComponent } from '../../components/calendar-filters/calendar-filters.component';
import { EventCalendarFiltersEnum } from '../../model/rooms-calendar.model';
import {
  CalendarFilters,
  RoomsCalendarService,
} from '../../services/rooms-calendar.service';

@Component({
  selector: 'bad-rooms-calendar-overview',
  imports: [WeekCalendarComponent, CalendarFiltersComponent],
  templateUrl: './rooms-calendar-overview.component.html',
  styleUrl: './rooms-calendar-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomsCalendarOverviewComponent implements OnInit, OnDestroy {
  readonly #roomsCalendarService = inject(RoomsCalendarService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);

  protected readonly roomsCalendarEvents =
    this.#roomsCalendarService.roomsCalendarData;
  protected readonly isLoading =
    this.#roomsCalendarService.isRoomsCalendarDataLoading;
  protected role: RoleType | null = null;
  protected sortDirection = signal<'asc' | 'desc'>('asc');
  protected initialDate = new Date();
  protected initialFilters: CalendarFilters | undefined;

  #currentWeekStart = '';
  #currentWeekEnd = '';

  ngOnInit() {
    this.role = this.#activatedRoute.snapshot.data['role'] as RoleType | null;
    this.#initFiltersFromQueryParams();
  }

  ngOnDestroy() {
    this.#roomsCalendarService.clearCalendarFilters();
  }

  protected onWeekChange(range: {
    startDate: Date;
    endDate: Date;
    selectedDate: Date;
    userInitiated: boolean;
  }) {
    this.#currentWeekStart = format(range.startDate, 'yyyy-MM-dd');
    this.#currentWeekEnd = format(range.endDate, 'yyyy-MM-dd');
    this.#loadEvents();
    this.#updateUrlWithDate();
  }

  protected onEventClick(event: CalendarEventClickEvent) {
    void this.#router.navigate(
      ['..', 'booking-request', event.roomDexId, event.event.bookingId],
      { relativeTo: this.#activatedRoute }
    );
  }

  protected onFiltersChange(filters: CalendarFilters) {
    this.sortDirection.set(filters.sort);
    this.#roomsCalendarService.filterEvents(filters);
    this.#roomsCalendarService.setCalendarFilters(filters);
    this.#updateUrlWithFilters(filters);
  }

  protected onFiltersCleared() {
    this.sortDirection.set('asc');
    this.#roomsCalendarService.clearCalendarFilters();
    this.#clearFilterQueryParams();
  }

  protected onSortChange(sort: 'asc' | 'desc') {
    this.sortDirection.set(sort);
  }

  #loadEvents() {
    if (this.#currentWeekStart && this.#currentWeekEnd) {
      this.#roomsCalendarService
        .getRoomsEvents(this.role, this.#currentWeekStart, this.#currentWeekEnd)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe();
    }
  }

  #paramToArray(value: string | null): string[] {
    return value ? value.split(',').filter(Boolean) : [];
  }

  #arrayToParam(value: string[] | undefined): string | null {
    return value?.length ? value.join(',') : null;
  }

  #initFiltersFromQueryParams() {
    const queryParams = this.#activatedRoute.snapshot.queryParamMap;
    const dateParam = queryParams.get('date');

    if (dateParam) {
      const date = new Date(dateParam);
      if (!isNaN(date.getTime())) {
        this.initialDate = date;
      }
    }

    const sortValue = queryParams.get('sort');
    const filters: CalendarFilters = {
      service: this.#paramToArray(
        queryParams.get('service')
      ) as RoomServicesEnum[],
      status:
        (queryParams.get('status') as EventCalendarFiltersEnum) ||
        EventCalendarFiltersEnum.ALL,
      siteName: this.#paramToArray(queryParams.get('siteName')),
      buildingName: this.#paramToArray(queryParams.get('buildingName')),
      floorName: this.#paramToArray(queryParams.get('floorName')),
      sort: (sortValue as 'asc' | 'desc') || 'asc',
    };

    const hasFilters =
      (filters.service && filters.service.length > 0) ||
      filters.status !== EventCalendarFiltersEnum.ALL ||
      (filters.siteName && filters.siteName.length > 0) ||
      (filters.buildingName && filters.buildingName.length > 0) ||
      (filters.floorName && filters.floorName.length > 0);

    if (hasFilters) {
      this.initialFilters = filters;
      this.sortDirection.set(filters.sort);
      this.#roomsCalendarService.filterEvents(filters);
      this.#roomsCalendarService.setCalendarFilters(filters);
    }
  }

  #updateUrlWithDate() {
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: { date: this.#currentWeekStart },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #updateUrlWithFilters(filters: CalendarFilters) {
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {
        service: this.#arrayToParam(filters.service),
        status:
          filters.status !== EventCalendarFiltersEnum.ALL
            ? filters.status
            : null,
        siteName: this.#arrayToParam(filters.siteName),
        buildingName: this.#arrayToParam(filters.buildingName),
        floorName: this.#arrayToParam(filters.floorName),
        sort: filters.sort !== 'asc' ? filters.sort : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #clearFilterQueryParams() {
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {
        service: null,
        status: null,
        siteName: null,
        buildingName: null,
        floorName: null,
        sort: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
